import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { resources } from "@constants/hardcoded-strings";
import { IDENTITY_PROVIDERS_DETAILS_URL } from "@constants/routes";
import { TableCellWithDropdown } from "@containers/common/table-cell-with-dropdown/table-cell-with-dropdown";
import { useModalContext } from "@context/use-modal-context";
import { ChangeRequestStatusTypes } from "@enums/change-request-status-types";
import { ModalTypes } from "@enums/modal-types";
import { CellDropdownItem } from "@idw-types/common";
import {
    FormattedIdentityProviderDetails,
    IdentityProviderChangeRequestResponse
} from "@idw-types/data/identity-providers";
import { IdentityProviderValidationErrors } from "@idw-types/common/validation/identity-provider/identity-provider-validation-errors";
import { createIdentityProviderChangeRequestRequest } from "@requests/identity-provider";
import { displaySuccessModal, logAndDisplayErrorModal } from "@utils/app-alert-util";
import { navigateToUtil } from "@utils/navigation-util";
import { injectCustomIdParameter } from "@utils/parameter-injector";
import { getRemainingChangeRequestStatuses } from "@utils/change-request-modal-util/common-change-request-modal-util";
import {
    clickIdentityProviderChangeRequestActionHandler,
    deployIdentityProvider,
    performIdentityProviderChangeRequestStep
} from "@utils/change-request-modal-util/identity-provider-change-request-modal-util";
import styles from "./identity-provider-details-dropdown-menu.less";
import { IDENTITY_PROVIDER_ID_PATTERN } from "@constants/url-parameter-patterns";
import { useChangeRequest } from "@hooks/use-change-requests";


type IdentityProviderDetailsDropdownMenuProps = {
    stateIsUnchanged: boolean;
    validationErrors: IdentityProviderValidationErrors;
    requestInProgress: boolean;
    identityProviderDetails: FormattedIdentityProviderDetails;
    identityProviderChangeRequests: Array<IdentityProviderChangeRequestResponse>;
    setRequestInProgress: (requestInProgress: boolean) => void;
    setIdentityProviderDetails: (idpDetails: FormattedIdentityProviderDetails) => void;
    setIdentityProviderChangeRequests: (changeRequest: IdentityProviderChangeRequestResponse) => void;
}

export const IdentityProviderDetailsDropdownMenu = (props: IdentityProviderDetailsDropdownMenuProps) => {
    const {
        stateIsUnchanged,
        validationErrors,
        identityProviderDetails,
        identityProviderChangeRequests,
        requestInProgress,
        setRequestInProgress,
        setIdentityProviderDetails,
        setIdentityProviderChangeRequests
    } = props;

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const routerLocation = useLocation();
    const modalContext = useModalContext();

    const {
        changeRequestStatus,
        changeRequestType
    } = useChangeRequest({ changeRequests: identityProviderChangeRequests });


    const redirectToIdentityProviderDetailsPage = () => {
        const url = injectCustomIdParameter(IDENTITY_PROVIDERS_DETAILS_URL, identityProviderDetails.id, IDENTITY_PROVIDER_ID_PATTERN);
        navigateToUtil(url, navigate, routerLocation.pathname);
    };

    const createIdentityProviderDetailChangeRequest = async () => {
        const { data, error } = await createIdentityProviderChangeRequestRequest(identityProviderDetails.id);
        if (data?.response) {
            displaySuccessModal(
                dispatch,
                resources.requests.createIdentityProviderChangeRequest.success.title,
                resources.requests.createIdentityProviderChangeRequest.success.message
            );

            setIdentityProviderChangeRequests(data.response);
            redirectToIdentityProviderDetailsPage();
            return data.response;
        } else if (error) {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.createIdentityProviderChangeRequest.error.title,
                resources.requests.createIdentityProviderChangeRequest.error.message,
                error
            );
        }
        return null;
    };

    const onSubmitCreateIdentityProviderChangeRequest = async (onModalClose: () => void) => {
        const response = await createIdentityProviderDetailChangeRequest();

        if (response) {
            onModalClose();
        }
        return response;
    }

    const onClickCreateAModifyChangeRequest = async () => {
        return clickIdentityProviderChangeRequestActionHandler(
            dispatch,
            onSubmitCreateIdentityProviderChangeRequest,
            modalContext,
            identityProviderDetails.id,
            identityProviderDetails.name,
            ModalTypes.CreateIdentityProviderChangeRequest
        );
    };

    const handleSuccessfulChangeRequestAction = (
        newChangeRequest: IdentityProviderChangeRequestResponse,
        onModalClose: () => void
    ) => {
        displaySuccessModal(
            dispatch,
            resources.requests.updateIdentityProviderChangeRequest.success.title,
            resources.requests.updateIdentityProviderChangeRequest.success.message
        );
        setIdentityProviderDetails(identityProviderDetails);
        setIdentityProviderChangeRequests(newChangeRequest);
        redirectToIdentityProviderDetailsPage();
        onModalClose();
    };

    const onSubmitSubmitIdentityProviderChangeRequest = async (onModalClose: () => void) => {
        const response = await performIdentityProviderChangeRequestStep(
            dispatch,
            identityProviderDetails.id,
            ChangeRequestStatusTypes.SUBMITTED,
            null
        );

        if (response) {
            handleSuccessfulChangeRequestAction(response, onModalClose);
        }

        return response;
    };

    const onClickSubmitChangeRequest = () => {
        clickIdentityProviderChangeRequestActionHandler(
            dispatch,
            onSubmitSubmitIdentityProviderChangeRequest,
            modalContext,
            identityProviderDetails.id,
            identityProviderDetails.name,
            ModalTypes.SubmitIdentityProviderChangeRequest
        );
    };

    const onSubmitUnsubmitIdentityProviderChangeRequest = async (onModalClose: () => void) => {
        const response = await performIdentityProviderChangeRequestStep(
            dispatch,
            identityProviderDetails.id,
            ChangeRequestStatusTypes.UNSUBMITTED,
            null
        );

        if (response) {
            handleSuccessfulChangeRequestAction(response, onModalClose);
        }

        return response;
    };

    const onClickUnsubmitChangeRequest = () => {
        clickIdentityProviderChangeRequestActionHandler(
            dispatch,
            onSubmitUnsubmitIdentityProviderChangeRequest,
            modalContext,
            identityProviderDetails.id,
            identityProviderDetails.name,
            ModalTypes.UnsubmitIdentityProviderChangeRequest
        );
    };

    const onClickReviewChangeRequest = async () => {
        if (changeRequestStatus !== ChangeRequestStatusTypes.UNDER_REVIEW) {
            setRequestInProgress(true);
            const response = await performIdentityProviderChangeRequestStep(
                dispatch,
                identityProviderDetails.id,
                ChangeRequestStatusTypes.UNDER_REVIEW,
                null
            );

            if (response) {
                setIdentityProviderDetails(identityProviderDetails);
                setIdentityProviderChangeRequests(response);
                redirectToIdentityProviderDetailsPage();
            }
            setRequestInProgress(false);
        } else {
            redirectToIdentityProviderDetailsPage();
        }
    };

    const onSubmitApproveIdentityProviderChangeRequest = async (onModalClose: () => void) => {
        const response = await performIdentityProviderChangeRequestStep(
            dispatch,
            identityProviderDetails.id,
            ChangeRequestStatusTypes.APPROVED,
            null
        );

        if (response) {
            handleSuccessfulChangeRequestAction(response, onModalClose);
        }
        return response;
    };

    const onClickApproveChanges = () => {
        clickIdentityProviderChangeRequestActionHandler(
            dispatch,
            onSubmitApproveIdentityProviderChangeRequest,
            modalContext,
            identityProviderDetails.id,
            identityProviderDetails.name,
            ModalTypes.ApproveIdentityProviderChangeRequest
        );
    };

    const onSubmitRejectIdentityProviderChangeRequest = async (onModalClose: () => void, data: string) => {
        const response = await performIdentityProviderChangeRequestStep(
            dispatch,
            identityProviderDetails.id,
            ChangeRequestStatusTypes.REJECTED,
            { comments: data }
        );

        if (response) {
            handleSuccessfulChangeRequestAction(response, onModalClose);
        }
        return response;
    };

    const onClickRejectChanges = () => {
        clickIdentityProviderChangeRequestActionHandler(
            dispatch,
            onSubmitRejectIdentityProviderChangeRequest,
            modalContext,
            identityProviderDetails.id,
            identityProviderDetails.name,
            ModalTypes.RejectIdentityProviderChangeRequest
        );
    };


    const onSubmitCancelIdentityProviderChangeRequest = async (onModalClose: () => void) => {
        const response = await performIdentityProviderChangeRequestStep(
            dispatch,
            identityProviderDetails.id,
            ChangeRequestStatusTypes.CANCELED,
            null
        );

        if (response) {
            handleSuccessfulChangeRequestAction(response, onModalClose);
        }

        return response;
    }


    const onClickCancelChangeRequest = async () => {
        clickIdentityProviderChangeRequestActionHandler(
            dispatch,
            onSubmitCancelIdentityProviderChangeRequest,
            modalContext,
            identityProviderDetails.id,
            identityProviderDetails.name,
            ModalTypes.CancelIdentityProviderChangeRequest
        )
    };

    const onSubmitDeployNowIdentityProviderChangeRequest = async (onModalClose: () => void) => {
        const remainingChangeRequestStatuses = getRemainingChangeRequestStatuses(changeRequestStatus);
        const response = await deployIdentityProvider(dispatch, identityProviderDetails.id, remainingChangeRequestStatuses, 0);

        if (response) {
            handleSuccessfulChangeRequestAction(response, onModalClose);
        }

        return response;
    }

    const onClickDeployChangesNow = async () => {
        clickIdentityProviderChangeRequestActionHandler(
            dispatch,
            onSubmitDeployNowIdentityProviderChangeRequest,
            modalContext,
            identityProviderDetails.id,
            identityProviderDetails.name,
            ModalTypes.DeployNowIdentityProviderChangeRequest
        );
    };

    const identityProviderDropdownItems = useMemo(() => {
        const items: Array<CellDropdownItem> = [];

        const {
            permissions
        } = identityProviderDetails;

        const prefix = 'identity-provider-details-dropdown-item';

        if ((
            permissions.canSubmit && permissions.canReview && permissions.canDeploy && changeRequestStatus &&
            [
                ChangeRequestStatusTypes.UNSUBMITTED,
                ChangeRequestStatusTypes.SUBMITTED,
                ChangeRequestStatusTypes.UNDER_REVIEW,
                ChangeRequestStatusTypes.APPROVED
            ].includes(changeRequestStatus)
        ) || (permissions.canDeploy && changeRequestType && changeRequestStatus && changeRequestStatus === ChangeRequestStatusTypes.APPROVED)) {
            let disabledText = resources.common.dropdownItems.deployChangesNow.unsavedChangesTooltip;
            if (validationErrors.count > 0) {
                disabledText = resources.common.dropdownItems.deployChangesNow.validationErrorsTooltip
            }

            items.push({
                idSuffix: `${prefix}-deploy-changes-now`,
                title: resources.common.dropdownItems.deployChangesNow.label,
                text: resources.common.dropdownItems.deployChangesNow.label,
                disabled: !stateIsUnchanged || validationErrors.count > 0,
                disabledText: disabledText,
                onClick: onClickDeployChangesNow
            });
        }

        if (permissions.canModify && !changeRequestType && !changeRequestStatus) {
            items.push({
                idSuffix: `${prefix}-create-modify-change-request`,
                title: resources.common.dropdownItems.createModifyChangeRequest.label,
                text: resources.common.dropdownItems.createModifyChangeRequest.label,
                onClick: onClickCreateAModifyChangeRequest
            });
        }

        if (permissions.canSubmit && changeRequestType && changeRequestStatus === ChangeRequestStatusTypes.UNSUBMITTED) {
            let disabledText = resources.common.dropdownItems.submitChangeRequestForReview.unsavedChangesTooltip;
            if (validationErrors.count > 0) {
                disabledText = resources.common.dropdownItems.submitChangeRequestForReview.validationErrorsTooltip
            }

            items.push({
                idSuffix: `${prefix}-submit-change-request`,
                title: resources.common.dropdownItems.submitChangeRequestForReview.label,
                text: resources.common.dropdownItems.submitChangeRequestForReview.label,
                disabled: !stateIsUnchanged || validationErrors.count > 0,
                disabledText: disabledText,
                onClick: onClickSubmitChangeRequest
            });
        }

        if (
            permissions.canSubmit &&
            changeRequestType &&
            [ChangeRequestStatusTypes.SUBMITTED, ChangeRequestStatusTypes.UNDER_REVIEW].includes(changeRequestStatus)
        ) {
            items.push({
                idSuffix: `${prefix}-unsubmit-change-request`,
                title: resources.common.dropdownItems.unsubmitChangeRequest.label,
                text: resources.common.dropdownItems.unsubmitChangeRequest.label,
                onClick: onClickUnsubmitChangeRequest
            });
        }

        if (
            permissions.canReview &&
            changeRequestType &&
            [ChangeRequestStatusTypes.SUBMITTED].includes(changeRequestStatus)
        ) {
            items.push(
                {
                    idSuffix: `${prefix}-review-change-request`,
                    title: resources.common.dropdownItems.reviewChangeRequest.label,
                    text: resources.common.dropdownItems.reviewChangeRequest.label,
                    onClick: onClickReviewChangeRequest
                }
            );
        }

        if (
            permissions.canReview &&
            changeRequestType &&
            [ChangeRequestStatusTypes.UNDER_REVIEW].includes(changeRequestStatus)
        ) {
            items.push(
                {
                    idSuffix: `${prefix}-approve-change-request`,
                    title: resources.common.dropdownItems.approveChangeRequest.label,
                    text: resources.common.dropdownItems.approveChangeRequest.label,
                    onClick: onClickApproveChanges
                },
                {
                    idSuffix: `${prefix}-reject-change-request`,
                    title: resources.common.dropdownItems.rejectChangeRequest.label,
                    text: resources.common.dropdownItems.rejectChangeRequest.label,
                    onClick: onClickRejectChanges
                },
            );
        }

        if (permissions.canCancel && changeRequestType && ![ChangeRequestStatusTypes.CANCELED, ChangeRequestStatusTypes.DEPLOYED].includes(changeRequestStatus)
        ) {
            items.push({
                idSuffix: `${prefix}-cancel-change-request`,
                title: resources.common.dropdownItems.cancelChangeRequest.label,
                text: resources.common.dropdownItems.cancelChangeRequest.label,
                onClick: onClickCancelChangeRequest
            });
        }

        return items;
    }, [
        identityProviderDetails,
        changeRequestStatus,
        changeRequestType,
        stateIsUnchanged
    ]);

    return (
        <TableCellWithDropdown
            className={styles.identityProviderDetailsDropdownMenu}
            value={""}
            dropdownText={"Actions"}
            disabled={requestInProgress}
            items={identityProviderDropdownItems}
            uniqueId={identityProviderDetails.id}
        />
    );

}